import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAllPosts, getDatePathFromIsoString, slugifyBlogTag } from '@/utils/blog';
import { BlogPostMetadata } from '@/types/blog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BlogCard from '@/components/BlogCard'; // Assuming you have a BlogCard component
import { ArrowLeft } from 'lucide-react';
import PageMeta from '@/components/seo/PageMeta';

function formatTagName(tagName?: string): string {
  if (!tagName) {
    return 'Tag';
  }

  return slugifyBlogTag(tagName)
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const BlogTagPage: React.FC = () => {
  const { tagName } = useParams<{ tagName: string }>();
  const [posts, setPosts] = useState<BlogPostMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPostsByTag = async () => {
      if (!tagName) return;
      setLoading(true);
      try {
        const allPosts = await getAllPosts();
        const normalizedTagName = slugifyBlogTag(tagName);
        const filteredPosts = allPosts.filter(post => 
          post.tags.some(tag => slugifyBlogTag(tag) === normalizedTagName)
        );
        setPosts(filteredPosts);
      } catch (error) {
        console.error('Error fetching posts by tag:', error);
        // Optionally, add toast notifications for errors
      } finally {
        setLoading(false);
      }
    };

    fetchPostsByTag();
  }, [tagName]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-950">
        <PageMeta
          title={`${formatTagName(tagName)} Articles And Viewing Tips | skore.info Blog`}
          description={`Browse skore.info blog posts tagged ${formatTagName(tagName)} for spoiler-free sports viewing tips, recap workflows, filtering advice, and product notes from the site.`}
          canonicalPath={`/blog/tags/${slugifyBlogTag(tagName || '')}`}
          keywords={[formatTagName(tagName), 'skore.info blog', 'spoiler-free sports', 'sports viewing tips']}
        />
        <Header />
        <main className="flex-1 py-8 px-4">
          <div className="container mx-auto max-w-3xl">
            <div className="h-8 w-3/4 animate-pulse rounded bg-gray-800 mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-64 animate-pulse rounded-lg bg-gray-800" />
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-950 text-white">
      <PageMeta
        title={`${formatTagName(tagName)} Articles And Viewing Tips | skore.info Blog`}
        description={`Browse skore.info blog posts tagged ${formatTagName(tagName)} for spoiler-free sports viewing tips, recap workflows, filtering advice, and product notes from the site.`}
        canonicalPath={`/blog/tags/${slugifyBlogTag(tagName || '')}`}
        keywords={[formatTagName(tagName), 'skore.info blog', 'spoiler-free sports', 'sports viewing tips']}
      />
      <Header />
      <main className="flex-1 py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-6">
            <Link
              to="/blog"
              className="inline-flex items-center text-indigo-400 hover:text-indigo-300"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Link>
          </div>
          <h1 className="mb-8 text-3xl md:text-4xl font-bold">
            Posts tagged with: <span className="text-indigo-400">#{formatTagName(tagName)}</span>
          </h1>
          {posts.length === 0 ? (
            <p className="text-gray-400">No posts found for this tag.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => {
                const datePath = getDatePathFromIsoString(post.date);
                const postUrl = `/blog/${datePath}/${post.slug}`;
                return <BlogCard key={post.slug} post={post} url={postUrl} />;
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogTagPage; 
